import {inject, Injectable} from '@angular/core';
import {TreeNode} from 'primeng/api';
import {map, Observable} from 'rxjs';
import {ChapterConfigService} from '../../pages/customer/project-sequence/chapter-config';
import {TranslatedOption} from './enum-translation-mapper.service';
import {ProjectStepType} from '@frontend/shared-api';
import {TranslocoService} from '@jsverse/transloco';
import {DirectusService, FlattenedProjectStep} from '@frontend/directus';
import {PageMapping} from '../model/page-tree.types';

@Injectable({
  providedIn: 'root',
})
export class PageTreeService {
  private pageKeyToEnumMap: Record<string, ProjectStepType> = {};
  private readonly transloco = inject(TranslocoService);
  private readonly directusService = inject(DirectusService);

  private readonly chapterConfigService = inject(ChapterConfigService);

  loadPageMappings(): Observable<PageMapping[]> {
    return this.chapterConfigService
      .loadChapterDefinitions()
      .pipe(map((chapters) => this.chapterConfigService.createPageMappingsFromChapters(chapters)));
  }

  getSelectedNodesFromPages(pages: string[], treeNodes: TreeNode[]): TreeNode[] {
    if (!pages || pages.length === 0) {
      return [];
    }

    const result: TreeNode[] = [];
    const parentsToInclude = new Set<string>();

    treeNodes.forEach((pageGroup) => {
      if (pageGroup.children) {
        let allChildrenSelected = true;

        pageGroup.children.forEach((node) => {
          const nodeKey = node.key as string;
          if (!nodeKey) return;

          const pageValue = this.pageKeyToEnumMap[nodeKey];
          if (!pageValue) return;

          if (pages.includes(pageValue as string)) {
            result.push(node);
          } else {
            allChildrenSelected = false;
          }
        });

        if (allChildrenSelected && pageGroup.children.length > 0) {
          parentsToInclude.add(pageGroup.key as string);
        }
      }
    });

    treeNodes.forEach((pageGroup) => {
      if (parentsToInclude.has(pageGroup.key as string)) {
        result.push(pageGroup);
      }
    });

    return result;
  }

  getLeafNodeKeys(nodes: TreeNode[]): string[] {
    if (!nodes || nodes.length === 0) {
      return [];
    }

    const leaves: string[] = [];
    const recurse = (node: TreeNode) => {
      if (!node.children?.length) {
        if (node.key) {
          leaves.push(node.key);
        }
      } else if (node.children.some((c) => nodes.some((s) => s.key === c.key))) {
        node.children.forEach((c) => nodes.some((s) => s.key === c.key) && recurse(c));
      } else {
        node.children.forEach((c) => recurse(c));
      }
    };
    nodes.forEach(recurse);
    return leaves;
  }

  getAccessibleStepsFromLeafKeys(leafKeys: string[]): ProjectStepType[] {
    return Array.from(new Set(leafKeys.map((k) => this.pageKeyToEnumMap[k]).filter(Boolean)));
  }

  setPageKeyToEnumMap(enumMap: Record<string, ProjectStepType>): void {
    this.pageKeyToEnumMap = enumMap;
  }

  convertToTreeNodesWithDirectus(
    options: TranslatedOption<ProjectStepType>[],
    pageMappings: PageMapping[],
    projectSteps: FlattenedProjectStep[],
  ): TreeNode[] {
    const treeNodes: TreeNode[] = [];
    const stepTranslationMap = new Map(options.map((opt) => [opt.enum, opt.translation]));
    const currentLang = this.transloco.getActiveLang();

    const chapterSteps = projectSteps.filter((step) => !step.parent_step);
    const childSteps = projectSteps.filter((step) => !!step.parent_step);

    const enumMap: Record<string, ProjectStepType> = {};

    for (const [i, mapping] of pageMappings.entries()) {
      const chapter = chapterSteps[i];
      const chapterTitle = this.getStepTitle(chapter, currentLang, mapping.label);

      const childTreeNodes = this.buildChildTreeNodes(
        mapping,
        childSteps,
        chapter,
        stepTranslationMap,
        currentLang,
        enumMap,
        i + 1,
      );

      if (childTreeNodes.length > 0) {
        treeNodes.push({
          key: mapping.key,
          label: `${i + 1}. ${chapterTitle}`,
          children: childTreeNodes,
        });
      }
    }

    this.pageKeyToEnumMap = enumMap;
    return treeNodes;
  }

  private getStepTitle(
    step: FlattenedProjectStep | undefined,
    lang: string,
    fallback: string,
  ): string {
    if (!step) return fallback;
    return step.translations?.find((t) => t.language === lang)?.name ?? step.title ?? fallback;
  }

  private buildChildTreeNodes(
    mapping: PageMapping,
    allChildSteps: FlattenedProjectStep[],
    chapter: FlattenedProjectStep | undefined,
    translationMap: Map<ProjectStepType, string>,
    lang: string,
    enumMap: Record<string, ProjectStepType>,
    chapterNumber: number,
  ): TreeNode[] {
    const treeNodes: TreeNode[] = [];

    const chapterChildSteps = chapter
      ? allChildSteps.filter((step) => step.parent_step === chapter.id)
      : [];

    mapping.subpages.forEach((stepType, index) => {
      const key = `${mapping.key}_${index + 1}`;
      enumMap[key] = stepType;

      const step = chapterChildSteps[index];
      const fallbackLabel = translationMap.get(stepType) ?? this.formatLabel(stepType.toString());

      const label = this.getStepTitle(step, lang, fallbackLabel);

      treeNodes.push({
        key,
        label: `${chapterNumber}.${index + 1}. ${label}`,
        selectable: true,
      });
    });

    return treeNodes;
  }

  loadProjectStepsFromDirectus(): Observable<FlattenedProjectStep[]> {
    return this.directusService.loadProjectSteps();
  }

  private formatLabel(val: string): string {
    return val
      .replace(/_/g, ' ')
      .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
  }
}
